/* Extend Impact's components; the native theme still owns overlays and focus. */
import { MegaMenuDisclosure, NavigationDrawer } from 'theme';

const desktop = window.matchMedia('(min-width: 1150px)');

class MTMegaMenu extends MegaMenuDisclosure {
  connectedCallback() {
    this._mtAbort?.abort();
    this._mtAbort = new AbortController();
    const { signal } = this._mtAbort;
    this.summaryElement.setAttribute('aria-expanded', String(this.open));
    this.contentElement.inert = !this.open;
    this.addEventListener('focusin', () => this._preloadImages(), { signal });
    this.summaryElement.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowDown') return;
      event.preventDefault();
      this.open = true;
      Promise.resolve(this._mtTransition).then(() => {
        if (this.open) this.contentElement.querySelector('a[href]')?.focus();
      });
    }, { signal });
    desktop.addEventListener('change', () => { if (!desktop.matches) this.open = false; }, { signal });
  }

  disconnectedCallback() {
    this._mtAbort?.abort();
    clearTimeout(this._hoverTimer);
    document.removeEventListener('click', this._detectClickOutsideListener);
    document.removeEventListener('keydown', this._detectEscKeyboardListener);
    document.removeEventListener('focusout', this._detectFocusOutListener);
    this.open = false;
  }

  _onSummaryClicked(event) {
    // A click after pointer entry keeps the hovered menu open. Keyboard and
    // touch input still toggle it normally.
    event.preventDefault();
    this.open = this.trigger === 'hover' && event.detail > 0 ? true : !this.open;
  }

  _detectHover(event) {
    if (this.contains(event.relatedTarget)) return;
    if (event.type === 'mouseout' && this.contains(document.activeElement) && document.activeElement.matches(':focus-visible')) return;
    super._detectHover(event);
  }

  _detectClickOutside(event) {
    if (!this.contains(event.target)) this.open = false;
  }

  _detectEscKeyboard(event) {
    if (event.key !== 'Escape' || !this.contains(event.target)) return;
    event.preventDefault();
    this.open = false;
    this.summaryElement.focus();
  }

  _transition(value) {
    this.summaryElement.setAttribute('aria-expanded', String(value));
    this.contentElement.inert = !value;
    if (value) this._preloadImages();
    // Serialize native animations when the pointer moves rapidly in and out.
    this._mtTransition = Promise.resolve(this._mtTransition).then(() => {
      if (!this.isConnected || value !== this.open) return;
      return super._transition(value);
    });
    return this._mtTransition;
  }
}

class MTNavigationDrawer extends NavigationDrawer {
  connectedCallback() {
    super.connectedCallback();
    this._mtAbort?.abort();
    this._mtAbort = new AbortController();
    // Keep one category expanded so the mobile menu stays easy to scan.
    this.addEventListener('toggle', (event) => {
      if (!event.target.matches('.mt-menu-accordion') || !event.target.open) return;
      this.querySelectorAll('.mt-menu-accordion[open]').forEach(category => {
        if (category !== event.target) category.open = false;
      });
    }, { capture: true, signal: this._mtAbort.signal });
    desktop.addEventListener('change', () => {
      if (desktop.matches) this.hide();
    }, { signal: this._mtAbort.signal });
  }

  disconnectedCallback() {
    this._mtAbort?.abort();
    super.disconnectedCallback();
  }

  get initialFocus() {
    return this.querySelector('[data-mt-close]') || super.initialFocus;
  }

  show(animate = true) {
    if (desktop.matches) return;
    this.querySelectorAll('img[loading="lazy"]').forEach(image => { image.loading = 'eager'; });
    return super.show(animate);
  }
}

if (!customElements.get('mt-mega-menu')) {
  customElements.define('mt-mega-menu', MTMegaMenu, { extends: 'details' });
}
if (!customElements.get('mt-navigation-drawer')) {
  customElements.define('mt-navigation-drawer', MTNavigationDrawer);
}
